import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Title } from "@angular/platform-browser";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet,
} from "@angular/router";
import { HardLoadingComponent } from "@hardcode-kz/ui";
import { IAppConfig } from "@models/core/setting";
import { LangEnum } from "@models/interface-management/localization";
import { ICurrentUser } from "@models/user-management/user";
import { AppService } from "@services/app/app.service";
import { LocalizationService } from "@app/core/localization";
import { BehaviorSubject, filter } from "rxjs";
import { APP_CONFIG } from "@tokens/application-config.token";
import { ToolbarModule } from "@layout/toolbar.module";
import { AuthService } from "./core";
import { ApplicationInsightsService } from "@app/core/error-handling";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
  standalone: true,
  imports: [RouterOutlet, HardLoadingComponent, ToolbarModule],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly localizationService = inject(LocalizationService);
  private readonly appService = inject(AppService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly titleService = inject(Title);
  private readonly appInsightsService = inject(ApplicationInsightsService);

  private readonly appConfig$ = inject<BehaviorSubject<IAppConfig>>(APP_CONFIG);

  loading = signal(true);
  user = signal<ICurrentUser>(null);
  init = signal(false);
  openPopover = signal(false);
  appConfig = signal<IAppConfig>(null);

  private intervalBlinking: ReturnType<typeof setInterval>;

  constructor() {
    const savedLang = (localStorage.getItem("lang") as LangEnum) || LangEnum.RU;
    this.localizationService.setLang(savedLang);
  }

  ngOnInit(): void {
    this.initRouteEvents();
    this.initAppConfig();

    this.authService.updateCurrentUser();

    this.authService.currentUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.user.set(user);
        if (user) {
          this.appInsightsService.setUser(user.login || user.id);
          this.init.set(true);
        } else {
          this.appInsightsService.clearUser();
          this.init.set(true);
        }
      });

    this.blinking();

    this.appService
      .subscribeOpenPopover()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((bool) => {
        this.openPopover.set(bool);
      });
  }

  private initRouteEvents(): void {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationStart),
      )
      .subscribe(() => {
        this.loading.set(true);
      });

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          (event) =>
            event instanceof NavigationEnd || event instanceof NavigationCancel,
        ),
      )
      .subscribe(() => {
        this.loading.set(false);
      });
  }

  private initAppConfig(): void {
    this.appConfig$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((appConfig) => {
        this.appConfig.set(appConfig);

        this.titleService.setTitle(
          `${appConfig?.applicationName ?? "Booking TCO"}`,
        );
      });
  }

  protected closePopover(): void {
    this.appService.toggleOpen(false);
  }

  private blinking(): void {
    let isBlinking = false;
    this.intervalBlinking = setInterval(() => {
      isBlinking = !isBlinking;

      const blinkingElements = document.body.querySelectorAll(".blinking");

      blinkingElements.forEach((element: HTMLElement) => {
        if (isBlinking) {
          element.classList.add("blinkingOn");
        } else {
          element.classList.remove("blinkingOn");
        }
      });
    }, 500);
  }

  ngOnDestroy(): void {
    this.appService.unsubscribes();

    if (this.intervalBlinking) {
      clearInterval(this.intervalBlinking);
    }
  }
}
